import { ColDef, GridOptions, GridReadyEvent } from '@ag-grid-community/core'
import { Component, Input } from '@angular/core'
import {
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  heliosClientSideRowConfig,
  heliosDefaultColDef,
} from '@helios/ag-grid-config'
import {
  DensityLevel,
  UserPreferencesService,
  WorkspaceGridSavedViewService,
} from '@helios/user-preferences'
import { AgGridApi } from '@northfork/ag-grid'
import { composeFieldPath } from '@northfork/basics'
import { Subject } from 'rxjs'
import { take, takeUntil } from 'rxjs/operators'
import { RulesKey } from '../../panel-key'
import { RulesAppliedTo } from '../models/rules.model'
import { RulesService } from '../services/rules-service/rules.service'

@Component({
  selector: 'hs-rule-applied-to',
  templateUrl: './rule-applied-to.component.html',
  styleUrls: ['./rule-applied-to.component.scss'],
})
export class RuleAppliedToComponent {
  _ruleIdSelected?: number
  ruleApplied: RulesAppliedTo[] = []
  isRuleAppliedLoading = false
  constructor(
    public rulesService: RulesService,
    private readonly preferences: UserPreferencesService,
    private readonly savedViewService: WorkspaceGridSavedViewService,
  ) {}
  rulesField = composeFieldPath(this.ruleApplied)
  gridApi?: AgGridApi
  private readonly destroySubject$ = new Subject<void>()

  @Input() set ruleIdSelected(value: number | undefined) {
    if (value) {
      this._ruleIdSelected = value
      this.displayRuleAppliedTo(value)
    }
  }

  displayRuleAppliedTo(ruleId: number) {
    this.isRuleAppliedLoading = true
    this.rulesService
      .getRuleAppliedTo(ruleId)
      .pipe(take(1))
      .subscribe(ruleApplied => {
        this.ruleAppliedDataWrapper(ruleApplied)
        this.isRuleAppliedLoading = false
      })
  }

  ruleAppliedDataWrapper(ruleApplied: RulesAppliedTo[]) {
    const rulesData: RulesAppliedTo[] = []
    ruleApplied.forEach((action: RulesAppliedTo) => {
      rulesData.push({
        ruleId: action.ruleId,
        dataFeedId: action.dataFeedId,
        parentClientId: action.parentClientId,
        accountId: action.accountId,
        category: action.category,
        name: action.name,
        clientId: action.clientId,
      })
    })
    this.ruleApplied = rulesData
  }

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
    this.gridApi?.api?.destroy()
  }

  gridOptions: GridOptions = {
    ...heliosClientSideRowConfig,
    defaultColDef: {
      ...heliosDefaultColDef,
      sortable: true,
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onFirstDataRendered: (params: any) => {
      params.api.sizeColumnsToFit()
    },
    rowSelection: 'single',
    suppressRowClickSelection: false,
    columnDefs: this.createColumnDefs(),

    onGridReady: params => this.onGridReady(params),
  }

  /** @internal */
  onGridReady(params: GridReadyEvent) {
    this.gridApi = params
    this.savedViewService.manageSavedView(
      params,
      RulesKey,
      this.destroySubject$,
    )
    this.preferences
      .getDensityLevel()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(level =>
        enableHeliosGridDensityLevel(
          params.api,
          DensityLevel.Compact === level,
        ),
      )

    enableHeliosCopyBehavior(this.gridApi.api)
  }

  private createColumnDefs(): ColDef[] {
    const colDef = [
      {
        field: this.rulesField('ruleId'),
        headerName: 'Rule ID',
      },

      {
        field: this.rulesField('category'),
      },

      {
        field: this.rulesField('name'),
      },
      {
        field: this.rulesField('accountId'),
        headerName: 'Account ID',
        width: 250,
      },
      {
        field: this.rulesField('dataFeedId'),
        headerName: 'Data Feed ID',
        initialHide: true,
      },
      {
        field: this.rulesField('parentClientId'),
        headerName: 'Parent Client ID',
      },
      {
        field: this.rulesField('clientId'),
        headerName: 'Client ID',
      },
    ]
    return colDef
  }
}
